/**
 * Global session-expired signal.
 *
 * Set by the tRPC fetch wrapper when a protected call ends in 401 and the silent
 * /auth/refresh also failed for a session we knew about; rendered once by
 * SessionExpiredDialog near the App root. Once expired nothing retries or refetches
 * until the user signs in again (a full page load resets it).
 */
type State = { expired: boolean; hadSession: boolean; amr: string | null };

let state: State = { expired: false, hadSession: false, amr: null };
const listeners = new Set<() => void>();

function set(next: State) {
  state = next;
  listeners.forEach((l) => l());
}

export const sessionStore = {
  subscribe(l: () => void) {
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  },
  isExpired: () => state.expired,
  amr: () => state.amr,
  /** A signed-in user is known (auth.me resolved). */
  setUser(amr: string | null) {
    if (state.hadSession && state.amr === amr && !state.expired) return;
    set({ expired: false, hadSession: true, amr });
  },
  /** Logout or no session: later 401s are not an expiry. */
  clearUser() {
    set({ expired: false, hadSession: false, amr: null });
  },
  /** Ignored unless a session was known: a logged-out visitor's 401 is just "not signed in". */
  markExpired() {
    if (state.hadSession && !state.expired) set({ ...state, expired: true });
  },
  reset() {
    set({ expired: false, hadSession: false, amr: null });
  },
};
